import 'whatwg-fetch'
import { checkStatus, parseJSON } from '../../js/modules/helper-functions'

export const settings = {
  selector: '.js-ajax-filter-post-list',
  filtersList: '.js-filters',
  topFilters: '.js-filters-top',
  subFilters: '.js-filters-sub',
  postsList: '.js-posts',
  lang: null,
  categories: [],
  selectedSub: null,
  selectedTop: null,
  buttons: [],
  subFiltersList: [],
  postsEl: null,
}

/**
 * Function initializes all elements in page.
 */
export default function initAll() {
  let elements = document.querySelectorAll( settings.selector )
  Array.from( elements ).forEach(( element ) => {
    init( element )
  })
}

/**
 * Function initializes single element.
 *
 * @param {DOMNode} element
 */
export async function init( element ) {
  // Set language
  settings.lang = element.dataset.lang

  // Get categories
  const categories = await getCategories()
  settings.categories = categories

  // Init top filters menu.
  const topFiltersEl = element.querySelector( settings.topFilters )
  const topCategories = categories.filter(( cat ) => cat.parent === 0 )
  if ( topCategories.length > 0 ) {
    settings.selectedTop = parseInt( topCategories[0].id, 10 )
    initTopFilters( topFiltersEl, topCategories )
  }

  // Init sub filter menus.
  const subFiltersEl = element.querySelector( settings.subFilters )
  topCategories.forEach(( topCat ) => {
    const childCategories = categories.filter(( cat ) => cat.parent === topCat.id )
    initSubFilters( subFiltersEl, childCategories, topCat )
  })
  setSubFilterStates()

  // Init posts list.
  const postsEl = element.querySelector( settings.postsList )
  settings.postsEl = postsEl
  renderPosts()
}

/**
 * Function retrieves all of the categories.
 */
function getCategories() {
  const lang = settings.lang
  const perPage = 100
  const hideEmpty = true
  const url = `/wp-json/wp/v2/categories?lang=${lang}&per_page=${perPage}&hide_empty=${hideEmpty}`
  return fetch( url )
    .then( checkStatus )
    .then( parseJSON )
    .catch(( error ) => {
      console.error( error )
      return []
    })
}

/**
 * Function retrieves posts based on
 * selected categories.
 */
function getPosts() {
  const lang = settings.lang
  let categories = []
  if ( settings.selectedSub !== null ) {
    categories.push( settings.selectedSub )
  } else {
    categories.push( settings.selectedTop )
  }
  const url = `/wp-json/wp/v2/posts?lang=${lang}&categories=${categories}`
  return fetch( url )
    .then( checkStatus )
    .then( parseJSON )
    .catch(( error ) => {
      console.error( error )
      return []
    })
}


/**
 * Function returns raw html markup for the
 * posts given in an array of `pids`.
 *
 * @param {array} pids
 */
function getPostsMarkups( pids ) {
  return fetch( '/wp-json/sponda/v1/archive-post-items-markup', {
    method: 'post',
    body: JSON.stringify({
      pids: pids
    }),
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then( checkStatus )
    .then( parseJSON )
    .catch(( error ) => {
      console.error( error )
      return []
    })
}

/**
 * Function renders the posts list
 * based on the posts it gets from `getPosts`
 * function and `getPostsMarkups` function.
 */
async function renderPosts() {
  const element = settings.postsEl
  element.innerHTML = ''
  const posts = await getPosts()
  if ( posts.length > 0 ) {
    const pids = posts.map(( p ) => p.id )
    const markups = await getPostsMarkups( pids )
    markups.forEach(( markup ) => {
      const li = document.createElement( 'li' )
      li.innerHTML = markup
      element.appendChild( li )
    })
  }
}

/**
 * Function initializes the top filters.
 *
 * @param {DOMNode} el
 * @param {array} categories
 */
function initTopFilters( el, categories ) {
  categories.forEach(( cat ) => {
    const li = document.createElement( 'li' )
    const btn = document.createElement( 'button' )
    const txt = document.createTextNode( cat.name )
    btn.dataset.category = cat.id
    btn.dataset.parent = 0
    li.dataset.category = cat.id
    btn.addEventListener( 'click', filterClick )
    settings.buttons.push( btn )
    setButtonStates()
    btn.appendChild( txt )
    li.appendChild( btn )
    el.appendChild( li )
  })
}

/**
 * Function initializes the sub filters.
 *
 * The function loops through all given categories
 * and creates new button elements based on those.
 * It will also attach needed event listeners and
 * set button states accordingly.
 *
 * @param {DOMNode} el
 * @param {array} categories
 * @param {object} topCategory
 */
function initSubFilters( el, categories, topCategory ) {
  const container = document.createElement( 'li' )
  container.classList.add( 'container' )
  container.dataset.parent = topCategory.id
  categories.forEach(( cat ) => {
    const btn = document.createElement( 'button' )
    const txt = document.createTextNode( cat.name )
    btn.dataset.category = cat.id
    btn.dataset.parent = topCategory.id
    btn.addEventListener( 'click', filterClick )
    settings.buttons.push( btn )
    setButtonStates()
    btn.appendChild( txt )
    container.appendChild( btn )
  })
  el.appendChild( container )
  settings.subFiltersList.push( container )
}

/**
 * Button click handler.
 *
 * Function will set the currently active
 * top filter category and sub filter category.
 *
 * It will also re-apply all button states,
 * render new posts and set the sub filters
 * visibility.
 */
function filterClick( event ) {
  const element = event.target
  const category = parseInt( element.dataset.category, 10 )
  const parent = parseInt( element.dataset.parent, 10 )
  if ( parent === 0 ) {
    settings.selectedTop = category
    settings.selectedSub = null
  } else {
    settings.selectedTop = parent
    settings.selectedSub = category
  }
  setButtonStates()
  renderPosts()
  setSubFilterStates()
}

/**
 * Function loops through all buttons
 * and sets them to disabled state if the
 * buttons category or parent category is
 * currently selected, otherwise the
 * disabled state will be removed.
 */
function setButtonStates() {
  settings.buttons.forEach(( button ) => {
    const category = parseInt( button.dataset.category, 10 )
    if ( settings.selectedTop === category || settings.selectedSub === category ) {
      button.setAttribute( 'disabled', '' )
    } else {
      button.removeAttribute( 'disabled' )
    }
  })
}

/**
 * Function loops through all of the subfilter elements
 * and checks if their parent is the selected top filter.
 * If the subfilers parent is not active, it will get
 * class `hidden` applied to it, otherwise the class
 * will be removed.
 */
function setSubFilterStates() {
  settings.subFiltersList.forEach(( element ) => {
    if ( settings.selectedTop !== parseInt( element.dataset.parent, 10 )) {
      element.classList.add( 'hidden' )
    } else {
      element.classList.remove( 'hidden' )
    }
  })

}
